import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc, setDoc, deleteDoc, onSnapshot, writeBatch } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useModuleAdmin } from '../useModuleAdmin';
import { ownerStamp } from '../recordOwner';
import { ACTOR_BUCKET_FOR, normaliseRole, isAdminRole } from '../roles';
import QRCode from 'qrcode';
import { Html5Qrcode } from 'html5-qrcode';
import Papa from 'papaparse';
import {
  UserCheck, BookOpen, Briefcase, GraduationCap, Settings as SettingsIcon,
  QrCode, ScanLine, IdCard, Camera, Edit3, X, MapPin, CheckCircle2, XCircle, ChevronDown, ChevronUp, ShieldCheck,
  Plus, Pencil, Trash2, CalendarX, Download, Save, RefreshCw, AlertTriangle,
} from 'lucide-react';
import HeaderActionsPortal from './HeaderActionsPortal';
import AttendanceScan from './AttendanceScan';
import AttendanceCards from './AttendanceCards';
import { useT } from '../i18n.jsx';
import { DAY_HALF, DEFAULT_CUTOFF, halfOf, halfOfRecord, blockingRecord, dayMarks, minutesOfDay, attendanceDocId } from '../attendanceDay.js';
import '../theme-palette.css';

// A QR code's payload is just { s: schoolId, id: qrId } -- every generation writes a qr_record doc
// server-side (generation time, generating user, school), and freshness at scan time is checked
// against THAT stored generatedAt rather than trusting anything embedded in the scanned payload.
// This also means a fabricated payload with a made-up id fails immediately (no matching qr_record),
// which is the actual "trace whether a QR is fake" mechanism -- not just an expiry check.
const QR_VALIDITY_MS = 5 * 60 * 1000;

const formatCountdown = (ms) => {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

// Local (not UTC) calendar day, so "today" always matches what the submitter actually experienced
// regardless of how far their timezone is from UTC.
const localDateString = (d = new Date()) => {
  const yr = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${yr}-${mo}-${da}`;
};

const pad2 = (n) => String(n).padStart(2, '0');
// Fixed yyyy-mm-dd hh:mm:ss (24-hour) formatting, independent of the viewer's browser locale.
const formatDateTime = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
};
const formatTime = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
};

// Buckets a raw profile role into exactly one of the three categories the Teacher/Staff/Student
// tabs filter on. Anything that isn't explicitly "student" or "teacher" (admin, developer, general
// staff, etc.) is treated as Staff.
const normalizeRoleBucket = (rawRole) => {
  const r = (rawRole || '').toLowerCase();
  if (r === 'student') return 'Student';
  if (r === 'teacher' || r === 'tch') return 'Teacher';
  if (r === 'parent') return 'Parent';
  return 'Staff';
};

// Older records (submitted before this field existed) fall back to deriving the bucket from
// submittedByRole, so nothing already in Firestore becomes invisible in these tabs.
const recordRoleBucket = (record) => record.role || normalizeRoleBucket(record.submittedByRole);

// Distinct from normalizeRoleBucket: this buckets the ACTOR (the person looking at/acting on
// records) into one of the four roles the Settings permission matrix is keyed on. Admin is its own
// bucket here (unlike the record-category bucket above, where admin-authored records fall under
// Staff) since admins are a distinct grantable actor role, not a record category.
// Maps a canonical role onto the permission-checkbox labels, which are stored in customer data and
// so are not migrated. Anything absent from the map -- parent, denied -- matches no permission list,
// which is exactly the intent: they hold no duty permissions of any kind.
const actorRoleBucket = (rawRole) => ACTOR_BUCKET_FOR[normaliseRole(rawRole)] || 'Denied';

const ACTOR_ROLES = ['Admin', 'Teacher', 'Staff'];
// Roles only. Individual people who need the action regardless of their role are named as
// module admins in Access Control -- see src/useModuleAdmin.js for why that replaced four
// separate per-action staff lists.
// bucket -- the "Others" row, for exceptions the three role-level checkboxes can't express.
const DEFAULT_PERMISSIONS = {
  Student: { edit: ['Admin'], verify: ['Admin', 'Teacher'] },
  Teacher: { edit: ['Admin'], verify: ['Admin'] },
  Staff: { edit: ['Admin'], verify: ['Admin'] },
};

const nowTimeHM = () => {
  const d = new Date();
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

// Shared table for the Teacher / Staff / Student tabs (also reused inside each per-class card on
// the Student tab). `canVerify`/`canEdit` gate whether those actions render at all for this viewer.
const AttendanceDayTable = ({ records, loading, canVerify, onVerify, canEdit, onEdit, onDelete, emptyText, studentMap, showStudentName, isMobile }) => {
  const { t } = useT();
  const methodWord = (m) => t(`attendance.methods.${String(m || '').toLowerCase().replace(/[^a-z]/g, '')}`, {}, m);
  return (
  <div className="table-container" style={{ backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', overflowX: 'auto' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
      <thead>
        <tr style={{ backgroundColor: 'var(--color-bg-app)', borderBottom: '1px solid var(--color-border)', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
          <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('attendance.name')}</th>
          <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('attendance.time')}</th>
          <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('attendance.method')}</th>
          <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('attendance.qrId')}</th>
          <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('attendance.ipAddress')}</th>
          <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('attendance.verifiedCol')}</th>
          <th style={{ padding: '0.75rem 1rem', fontWeight: '600', width: '180px' }}>{t('attendance.actions')}</th>
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>{t('common.loading')}</td></tr>
        ) : records.length === 0 ? (
          <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>{emptyText}</td></tr>
        ) : records.map((r, i) => {
          const student = showStudentName ? studentMap?.[r.submittedBySid] : null;
          const displayName = showStudentName
            ? (student ? `${student.classNumber ? student.classNumber + ' - ' : ''}${student.name}${student.chineseName ? ` (${student.chineseName})` : ''}` : (r.submittedByName || r.submittedBySid || '-'))
            : (r.submittedByName || r.submittedByInitial || '-');
          return (
            <tr key={r.id} style={{ borderBottom: i === records.length - 1 ? 'none' : '1px solid var(--color-border)', fontSize: '0.875rem' }}>
              <td style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{displayName}</td>
              <td style={{ padding: '0.75rem 1rem' }}>
                {formatTime(r.submittedAt)}
                <span style={{ display: 'block', fontSize: '0.72rem', color: 'var(--color-text-muted)' }}>
                  {t(`attendance.halves.${halfOfRecord(r)}`)}
                </span>
              </td>
              <td style={{ padding: '0.75rem 1rem' }}>{r.method ? methodWord(r.method) : '-'}</td>
              <td style={{ padding: '0.75rem 1rem', color: 'var(--color-text-muted)', fontSize: '0.8rem', fontFamily: 'monospace' }}>{r.method === 'QR' ? (r.qrId || '-') : '-'}</td>
              <td style={{ padding: '0.75rem 1rem', color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', gap: '0.3rem' }}><MapPin size={13} />{r.submitIp || t('attendance.unknownIp')}</td>
              <td style={{ padding: '0.75rem 1rem' }}>
                {r.verified ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', color: 'var(--color-success)', fontSize: '0.8rem', fontWeight: '600' }}><CheckCircle2 size={14} /> {t('attendance.verified')}</span>
                ) : (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', color: 'var(--color-text-muted)', fontSize: '0.8rem', fontWeight: '600' }}><XCircle size={14} /> {t('attendance.pending')}</span>
                )}
              </td>
              <td style={{ padding: '0.75rem 1rem' }}>
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                  {canVerify && !r.verified && (
                    <button onClick={() => onVerify(r)} className="btn-outline" style={{ width: 'auto', padding: '0.3rem 0.6rem', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.3rem', color: 'var(--color-success)', borderColor: 'var(--color-success)' }}>
                      <ShieldCheck size={14} /> {t('attendance.verify')}
                    </button>
                  )}
                  {canEdit && (
                    <button onClick={() => onEdit(r)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary)', display: 'flex' }} title={t('common.edit')}>
                      <Pencil size={16} />
                    </button>
                  )}
                  {canEdit && (
                    <button onClick={() => onDelete(r)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger)', display: 'flex' }} title={t('common.delete')}>
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
  );
};

const Attendance = () => {
  const { activeSchoolId, userProfile, isMobile } = useOutletContext();
  const { t } = useT();
  // Stored words. The value on the record never changes; these turn it into something to read.
  const slug = (v) => String(v || '').toLowerCase().replace(/[^a-z]/g, '');
  const methodWord = (m) => t(`attendance.methods.${slug(m)}`, {}, m);
  const statusWord = (v) => t(`attendance.leaveStatuses.${slug(v)}`, {}, v);
  const leaveTypeWord = (v) => t(`attendance.leaveTypes.${slug(v)}`, {}, v);
  const categoryWord = (v) => t(`attendance.categories.${slug(v)}`, {}, v);
  const [activeTab, setActiveTab] = useState('my_attendance');
  const [academicYears, setAcademicYears] = useState([]);
  const [schoolYear, setSchoolYear] = useState('');

  const ownEmail = (auth.currentUser?.email || '').toLowerCase();
  const ownInitial = userProfile?.initial || '';
  const ownSid = userProfile?.studentId || userProfile?.sid || '';
  const ownName = userProfile?.name || auth.currentUser?.email || 'Unknown';
  const ownRole = (userProfile?.role || '').toLowerCase();
  const isStudent = ownRole === 'student';
  // False for everyone on the first render after a reload, so anything keyed on "not a student"
  // must wait for the profile before it runs.
  const roleKnown = !!userProfile && !!ownRole;
  const isAdmin = isAdminRole(ownRole);
  const ownActorRole = actorRoleBucket(ownRole);
  // Named admins of this module: the one list the rules read, replacing the per-action
  // staff pickers that used to live in this module's own settings document.
  const isModuleAdmin = useModuleAdmin(activeSchoolId, 'attendance');

  // --- Settings: who can edit (add/update/delete) and verify each record category ---
  const [permissions, setPermissions] = useState(DEFAULT_PERMISSIONS);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  // Where the morning ends. A scan before it is a sign-in, from it on a sign-out.
  const [halfCutoff, setHalfCutoff] = useState(DEFAULT_CUTOFF);
  const [savingSettings, setSavingSettings] = useState(false);

  const fetchAttendanceSettings = async () => {
    if (!activeSchoolId) return;
    setSettingsLoaded(false);
    try {
      const snap = await getDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'attendance_settings'));
      const data = snap.exists() ? snap.data().permissions : null;
      const merge = (category) => ({
        edit: data?.[category]?.edit || DEFAULT_PERMISSIONS[category].edit,
        verify: data?.[category]?.verify || DEFAULT_PERMISSIONS[category].verify,
        // Roles only. Named individuals are module admins -- see src/useModuleAdmin.js.
      });
      setPermissions({ Student: merge('Student'), Teacher: merge('Teacher'), Staff: merge('Staff') });
      setHalfCutoff(snap.exists() ? (snap.data().halfCutoff || DEFAULT_CUTOFF) : DEFAULT_CUTOFF);
    } catch (e) {
      console.error('Error loading attendance settings', e);
    } finally {
      setSettingsLoaded(true);
    }
  };
  useEffect(() => { fetchAttendanceSettings(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [activeSchoolId]);

  const togglePermission = (category, action, role) => {
    setPermissions(prev => {
      const current = prev[category]?.[action] || [];
      const next = current.includes(role) ? current.filter(r => r !== role) : [...current, role];
      return { ...prev, [category]: { ...prev[category], [action]: next } };
    });
  };

  // "Others" = specific staff granted an action regardless of their role bucket, for exceptions
  // the three role checkboxes can't express (e.g. one particular teacher trusted with Staff edits).
  const [showOthersModal, setShowOthersModal] = useState(null); // { category, action } | null
  const [othersSearch, setOthersSearch] = useState('');

  const toggleOthersStaff = (category, action, email) => {
    setPermissions(prev => {
      const key = `${action}Staff`;
      const current = prev[category]?.[key] || [];
      const next = current.includes(email) ? current.filter(e => e !== email) : [...current, email];
      return { ...prev, [category]: { ...prev[category], [key]: next } };
    });
  };

  const handleSaveAttendanceSettings = async () => {
    if (minutesOfDay(halfCutoff) === null) { alert(t('attendance.badCutoff')); return; }
    setSavingSettings(true);
    try {
      await setDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'attendance_settings'), {
        permissions,
        halfCutoff,
        updatedAt: new Date().toISOString(),
        updatedBy: auth.currentUser?.email || 'Unknown',
      }, { merge: true });
      await fetchAttendanceSettings();
      alert(t('attendance.settingsSaved'));
    } catch (e) {
      console.error('Error saving attendance settings', e);
      alert(t('attendance.settingsFailed', { message: e.message }));
    }
    setSavingSettings(false);
  };

  // Admins always have full access regardless of the configured matrix -- a safety net against an
  // accidental misconfiguration locking everyone (including admins) out.
  const canEditCategory = (category) =>
    isAdmin
    || isModuleAdmin
    || (permissions[category]?.edit || []).includes(ownActorRole);
  const canVerifyCategory = (category) =>
    isAdmin
    || isModuleAdmin
    || (permissions[category]?.verify || []).includes(ownActorRole);

  // --- My Attendance: submission + own record history ---
  const [submitting, setSubmitting] = useState(false);
  const [myRecords, setMyRecords] = useState([]);
  const [loadingMyRecords, setLoadingMyRecords] = useState(false);
  const [showScanModal, setShowScanModal] = useState(false);
  const [scanError, setScanError] = useState('');
  const [hasCamera, setHasCamera] = useState(true);
  const html5QrRef = useRef(null);
  const scanHandledRef = useRef(false);
  const qrRegionId = 'attendance-qr-reader';

  // Best-effort check so the Scan QR Code button isn't clickable on a device with no camera at
  // all -- stays optimistic (button enabled) if the browser doesn't support device enumeration or
  // the check itself fails, since that's not the same as confirming there's no camera.
  useEffect(() => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    navigator.mediaDevices.enumerateDevices()
      .then(devices => setHasCamera(devices.some(d => d.kind === 'videoinput')))
      .catch(() => { /* leave optimistic */ });
  }, []);

  // --- Teacher / Staff / Student tabs: shared day filter + records for that day ---
  const [selectedDate, setSelectedDate] = useState(() => localDateString());
  const [dayRecords, setDayRecords] = useState([]);
  const [loadingDayRecords, setLoadingDayRecords] = useState(false);
  const [studentList, setStudentList] = useState([]);
  const [expandedClasses, setExpandedClasses] = useState(new Set());

  const [staffList, setStaffList] = useState([]);

  useEffect(() => {
    // Students only ever use My Attendance, which reads their own records -- they have no use for
    // the roll, and student_profiles is no longer readable school-wide.
    if (!activeSchoolId || !roleKnown || isStudent) return;
    getDocs(collection(db, 'schools', activeSchoolId, 'student_profiles'))
      .then(snap => {
        const list = [];
        snap.forEach(d => {
          const data = d.data();
          if (!data.sid) return;
          list.push({
            sid: data.sid,
            email: d.id,
            name: `${data.lastName || ''} ${data.firstName || ''}`.trim() || data.name || 'Unknown',
            chineseName: data.chineseName || '',
            currentClass: data.currentClass || '',
            classNumber: data.classNumber || '',
          });
        });
        setStudentList(list);
      })
      .catch(e => console.error('Error loading student list', e));
  }, [activeSchoolId, roleKnown, isStudent]);

  useEffect(() => {
    if (!activeSchoolId || !roleKnown || isStudent) return;
    getDocs(collection(db, 'schools', activeSchoolId, 'staff_profiles'))
      .then(snap => {
        const list = [];
        snap.forEach(d => {
          const data = d.data();
          list.push({
            email: d.id,
            initial: data.initial || '',
            name: `${data.lastName || ''} ${data.firstName || ''}`.trim() || data.displayName || d.id,
            role: (data.role || '').toLowerCase(),
          });
        });
        list.sort((a, b) => a.name.localeCompare(b.name));
        setStaffList(list);
      })
      .catch(e => console.error('Error loading staff list', e));
  }, [activeSchoolId, roleKnown, isStudent]);

  const studentMap = useMemo(() => Object.fromEntries(studentList.map(s => [s.sid, s])), [studentList]);
  const classOptions = useMemo(() => [...new Set(studentList.map(s => s.currentClass))].filter(Boolean).sort(), [studentList]);
  const teacherStaffOptions = useMemo(() => staffList.filter(s => s.role === 'teacher' || s.role === 'tch'), [staffList]);
  const nonTeacherStaffOptions = useMemo(() => staffList.filter(s => s.role !== 'teacher' && s.role !== 'tch' && s.role !== 'student'), [staffList]);
  const othersSearchResults = useMemo(() => {
    const q = othersSearch.trim().toLowerCase();
    if (!q) return staffList;
    return staffList.filter(s => s.name.toLowerCase().includes(q) || s.initial.toLowerCase().includes(q) || s.email.toLowerCase().includes(q));
  }, [staffList, othersSearch]);

  useEffect(() => {
    if (!activeSchoolId || !selectedDate || !['teacher', 'staff', 'student', 'leave'].includes(activeTab)) return;
    setLoadingDayRecords(true);
    const q = query(collection(db, 'schools', activeSchoolId, 'attendance_record'), where('submittedDateLocal', '==', selectedDate));
    const unsub = onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach(d => list.push({ id: d.id, ...d.data() }));
      list.sort((a, b) => (a.submittedAt || '').localeCompare(b.submittedAt || ''));
      setDayRecords(list);
      setLoadingDayRecords(false);
    }, (e) => { console.error('Error loading day attendance records', e); setLoadingDayRecords(false); });
    return () => unsub();
  }, [activeSchoolId, selectedDate, activeTab]);

  // --- Leave audit ----------------------------------------------------------
  //
  // Attendance only ever proves someone WAS here. Everything else -- a missed sign-in, an early
  // departure, an approved absence -- has to be asserted by whoever runs the daily check, so each
  // row starts from the attendance evidence and stays editable until it is confirmed.
  const LEAVE_STATUSES = ['Present', 'Late', 'Early Leave', 'Absent', 'On Leave'];
  const LEAVE_TYPES = ['Sick Leave', 'Annual Leave', 'No Pay Leave', 'Official Duty', 'Maternity / Paternity', 'Compensation Leave', 'Other'];
  // Statuses that assert the person was physically present. Saving one of these for somebody with no
  // check-in creates the missing attendance record on their behalf, so the Teacher/Staff tabs and the
  // audit can never disagree about whether a person turned up.
  const PRESENT_STATUSES = ['Present', 'Late', 'Early Leave'];

  const [leaveRows, setLeaveRows] = useState([]);
  const [loadingLeave, setLoadingLeave] = useState(false);
  const [savingLeave, setSavingLeave] = useState(false);
  const [calendarEvents, setCalendarEvents] = useState([]);

  useEffect(() => {
    if (!activeSchoolId || !schoolYear) return;
    getDocs(query(collection(db, 'schools', activeSchoolId, 'calendar_events'), where('yearId', '==', schoolYear)))
      .then(snap => {
        const list = [];
        snap.forEach(d => list.push(d.data()));
        setCalendarEvents(list);
      })
      .catch(e => console.error('Error loading calendar events', e));
  }, [activeSchoolId, schoolYear]);

  const isSchoolDay = useMemo(
    () => calendarEvents.some(e => e.type === 'School Day' && e.date === selectedDate),
    [calendarEvents, selectedDate]
  );

  // Everyone who is expected to sign in -- students are audited on their own tab, not here.
  const leavePeople = useMemo(
    () => staffList
      .filter(s => s.role !== 'student' && s.initial)
      .map(s => ({
        initial: s.initial,
        name: s.name,
        email: s.email,
        category: (s.role === 'teacher' || s.role === 'tch') ? 'teacher' : 'staff',
      }))
      .sort((a, b) => a.initial.localeCompare(b.initial)),
    [staffList]
  );

  // dayRecords is a live subscription, so reading it through a ref keeps loadLeave's identity stable.
  // Depending on it directly would rebuild loadLeave on every snapshot and re-run the effect below,
  // wiping whatever the auditor had typed but not yet saved the moment anyone else checked in.
  const dayRecordsRef = useRef([]);
  useEffect(() => { dayRecordsRef.current = dayRecords; }, [dayRecords]);

  const loadLeave = React.useCallback(async () => {
    if (!activeSchoolId || !selectedDate || leavePeople.length === 0) return;
    setLoadingLeave(true);
    try {
      const snap = await getDocs(query(
        collection(db, 'schools', activeSchoolId, 'leave_record'),
        where('date', '==', selectedDate)
      ));
      const saved = {};
      snap.forEach(d => { saved[d.data().initial] = d.data(); });

      // Keep the EARLIEST check-in per person: someone who scans twice should not look like a late
      // arrival because the second scan happened after the deadline.
      // Two marks per person now, so group the day's records and read both off each group.
      const byPerson = {};
      dayRecordsRef.current.forEach(r => {
        const key = r.submittedByInitial;
        if (!key || recordRoleBucket(r) === 'Student') return;
        (byPerson[key] = byPerson[key] || []).push(r);
      });
      const attendance = {};
      Object.entries(byPerson).forEach(([key, list]) => {
        const marks = dayMarks(list);
        // The arrival is what the audit calls the check-in. `dayMarks` keeps the EARLIEST of them,
        // so a repeat morning scan still cannot make somebody look late.
        const arrival = marks.in || marks.out;
        attendance[key] = {
          id: arrival.id,
          submittedAt: arrival.submittedAt || '',
          method: arrival.method || '',
          verified: !!arrival.verified,
          signOutAt: marks.out ? marks.out.submittedAt : '',
        };
      });

      setLeaveRows(leavePeople.map(p => {
        const att = attendance[p.initial];
        const rec = saved[p.initial];
        return {
          ...p,
          recordId: att ? att.id : null,
          checkIn: att ? new Date(att.submittedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '',
          method: att ? att.method : '',
          signOut: att && att.signOutAt
            ? new Date(att.signOutAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
            : '',
          hasAttendance: !!att,
          attendanceVerified: att ? att.verified : false,
          // A saved audit always wins -- it represents a human decision. Only fall back to the raw
          // attendance evidence for rows nobody has ruled on yet.
          status: rec?.status || (att ? 'Present' : 'Absent'),
          leaveType: rec?.leaveType || '',
          remark: rec?.remark || '',
          confirmed: !!rec?.confirmed,
        };
      }));
    } catch (e) {
      console.error('Error loading leave records', e);
      alert(t('attendance.loadLeaveFailed', { message: e.message }));
    }
    setLoadingLeave(false);
  }, [activeSchoolId, selectedDate, leavePeople]);

  // Waits for the day's attendance to finish loading (loadingDayRecords flipping false) before
  // building the rows, otherwise everyone briefly reads as "No record" on first paint.
  useEffect(() => {
    if (activeTab === 'leave' && !loadingDayRecords) loadLeave();
  }, [activeTab, loadingDayRecords, loadLeave]);

  // Editing a row IS the audit decision -- someone changing "Absent" to "Present" has just ruled on
  // that person, so the row confirms itself. (It previously cleared the flag, which meant an edit
  // followed by Save left the row sitting at Pending with no obvious way to clear it.) The checkbox
  // in the Confirmed column stays available for untick-and-come-back-later.
  const updateLeaveRow = (initial, patch) => {
    setLeaveRows(prev => prev.map(r => (r.initial === initial ? { ...r, ...patch, confirmed: true } : r)));
  };

  const setLeaveConfirmed = (initial, confirmed) => {
    setLeaveRows(prev => prev.map(r => (r.initial === initial ? { ...r, confirmed } : r)));
  };

  const handleSaveLeave = async (confirmAll = false) => {
    if (!activeSchoolId || !selectedDate) return;
    setSavingLeave(true);
    try {
      const batch = writeBatch(db);
      const now = new Date().toISOString();
      const by = auth.currentUser?.email || '';
      // Time the admin stamped the record -- used as the check-in for anyone they mark present who
      // never signed in themselves.
      const stampedTime = new Date(now).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

      leaveRows.forEach(r => {
        const assertsPresent = PRESENT_STATUSES.includes(r.status);
        // The module keeps Edit and Verify as separate rights, so someone delegated the leave audit
        // without Verify still records the attendance -- it just stays pending a verifier.
        const canVerify = canVerifyCategory(r.category === 'teacher' ? 'Teacher' : 'Staff');
        let { checkIn, method, hasAttendance } = r;

        if (assertsPresent && !r.hasAttendance) {
          // Nobody checked this person in, but an admin says they were here: create the attendance
          // record they are missing, timed at this update and pre-verified, since an admin asserting
          // it IS the verification.
          batch.set(doc(collection(db, 'schools', activeSchoolId, 'attendance_record')), {
            ...ownerStamp(),
            submittedBySid: '',
            submittedByEmail: r.email || '',
            submittedByInitial: r.initial,
            submittedByName: r.name,
            submittedByRole: r.category,
            role: r.category === 'teacher' ? 'Teacher' : 'Staff',
            submittedAt: now,
            submittedDateLocal: selectedDate,
            method: 'Admin',
            schoolYear,
            submitIp: 'N/A',
            qrId: null,
            verified: canVerify,
            ...(canVerify ? { verifiedByEmail: by, verifiedByName: ownName, verifiedAt: now } : {}),
            enteredByEmail: by,
            enteredByName: ownName,
            enteredAt: now,
          });
          checkIn = stampedTime;
          method = 'Admin';
          hasAttendance = true;
        }
        // Saving the audit does NOT verify a check-in that is already there.
        //
        // It used to: any row with a record defaulted to Present, so one Save with nothing touched
        // silently verified every pending check-in for the day -- and it verified the EARLIEST
        // record, which is the one `loadLeaveRecords` keeps. A self-scan is the person's own claim
        // that they arrived; somebody has to stand there and say it counts, and that is the Verify
        // button, one record at a time. Saving an audit is not that act.
        //
        // The branch above is different and stays: nobody checked that person in at all, so the
        // admin marking them present IS the only evidence, and it is stamped verified as such.

        batch.set(doc(db, 'schools', activeSchoolId, 'leave_record', `${selectedDate}__${r.initial}`), {
          date: selectedDate,
          schYr: schoolYear,
          initial: r.initial,
          name: r.name,
          email: r.email || '',
          category: r.category,
          checkIn,
          method,
          hasAttendance,
          status: r.status,
          leaveType: r.leaveType,
          remark: r.remark,
          confirmed: confirmAll ? true : r.confirmed,
          confirmedBy: by,
          confirmedAt: now,
        }, { merge: true });
      });

      await batch.commit();
      await loadLeave();
      alert(confirmAll ? t('attendance.leaveConfirmed') : t('attendance.leaveSaved'));
    } catch (e) {
      console.error('Error saving leave', e);
      alert(t('attendance.saveLeaveFailed', { message: e.message }));
    }
    setSavingLeave(false);
  };

  const leaveSummary = useMemo(() => ({
    total: leaveRows.length,
    missing: leaveRows.filter(r => !r.hasAttendance).length,
    confirmed: leaveRows.filter(r => r.confirmed).length,
  }), [leaveRows]);

  const exportLeaveCsv = () => {
    const columns = ['date', 'initial', 'name', 'category', 'checkIn', 'signOut', 'method', 'status', 'leaveType', 'remark', 'confirmed'];
    const rows = leaveRows.length ? leaveRows.map(r => ({
      date: selectedDate, initial: r.initial, name: r.name, category: r.category,
      checkIn: r.checkIn, signOut: r.signOut, method: r.method, status: r.status, leaveType: r.leaveType,
      remark: r.remark, confirmed: r.confirmed ? 'Y' : 'N',
    })) : [Object.fromEntries(columns.map(c => [c, '']))];
    const csv = Papa.unparse(rows, { columns });
    // BOM so Excel opens the name column in the correct encoding.
    const blob = new Blob([new Uint8Array([0xEF, 0xBB, 0xBF]), csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `leave_${selectedDate}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const teacherDayRecords = useMemo(() => dayRecords.filter(r => recordRoleBucket(r) === 'Teacher'), [dayRecords]);
  const staffDayRecords = useMemo(() => dayRecords.filter(r => recordRoleBucket(r) === 'Staff'), [dayRecords]);
  const studentDayRecords = useMemo(() => dayRecords.filter(r => recordRoleBucket(r) === 'Student'), [dayRecords]);

  const studentGroupsByClass = useMemo(() => {
    const groups = {};
    studentDayRecords.forEach(r => {
      const cls = studentMap[r.submittedBySid]?.currentClass || 'Unknown Class';
      (groups[cls] = groups[cls] || []).push(r);
    });
    return Object.keys(groups)
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
      .map(cls => ({ className: cls, records: groups[cls] }));
  }, [studentDayRecords, studentMap]);

  const toggleClassExpanded = (cls) => {
    setExpandedClasses(prev => {
      const next = new Set(prev);
      if (next.has(cls)) next.delete(cls); else next.add(cls);
      return next;
    });
  };

  const handleVerify = async (record) => {
    try {
      await setDoc(doc(db, 'schools', activeSchoolId, 'attendance_record', record.id), {
        verified: true,
        verifiedByEmail: auth.currentUser?.email || '',
        verifiedByName: ownName,
        verifiedAt: new Date().toISOString(),
      }, { merge: true });
    } catch (e) {
      console.error('Error verifying attendance record', e);
      alert(t('attendance.verifyFailed', { message: e.message }));
    }
  };

  // --- Add / Edit / Delete a record on behalf of someone else (authorized roles only) ---
  const emptyRecordForm = (category) => ({ category, personKey: '', classFilter: '', date: selectedDate, time: nowTimeHM(), method: 'Admin' });
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [recordForm, setRecordForm] = useState(emptyRecordForm('Teacher'));
  const [editingRecordId, setEditingRecordId] = useState(null);
  const [savingRecord, setSavingRecord] = useState(false);

  const openAddRecordModal = (category) => {
    setEditingRecordId(null);
    setRecordForm(emptyRecordForm(category));
    setShowRecordModal(true);
  };

  const openEditRecordModal = (category, record) => {
    setEditingRecordId(record.id);
    const d = record.submittedAt ? new Date(record.submittedAt) : new Date();
    const student = category === 'Student' ? studentMap[record.submittedBySid] : null;
    setRecordForm({
      category,
      personKey: category === 'Student' ? (record.submittedBySid || '') : (record.submittedByEmail || ''),
      classFilter: student?.currentClass || '',
      date: record.submittedDateLocal || localDateString(d),
      time: `${pad2(d.getHours())}:${pad2(d.getMinutes())}`,
      method: record.method || 'Admin',
    });
    setShowRecordModal(true);
  };

  const recordFormClassStudents = useMemo(
    () => studentList.filter(s => s.currentClass === recordForm.classFilter),
    [studentList, recordForm.classFilter]
  );

  const handleSaveRecordModal = async (e) => {
    e.preventDefault();
    if (!recordForm.personKey) return alert(t('attendance.needPerson'));
    if (!recordForm.date || !recordForm.time) return alert(t('attendance.needDateTime'));
    setSavingRecord(true);
    try {
      const [hh, mm] = recordForm.time.split(':').map(Number);
      const [yy, mo, dd] = recordForm.date.split('-').map(Number);
      const submittedAt = new Date(yy, mo - 1, dd, hh, mm, 0).toISOString();

      let personFields;
      if (recordForm.category === 'Student') {
        const student = studentMap[recordForm.personKey];
        personFields = {
          submittedBySid: recordForm.personKey,
          submittedByEmail: student?.email || '',
          submittedByInitial: '',
          submittedByName: student ? `${student.name}${student.chineseName ? ` (${student.chineseName})` : ''}` : '',
        };
      } else {
        const person = staffList.find(s => s.email === recordForm.personKey);
        personFields = {
          submittedBySid: '',
          submittedByEmail: recordForm.personKey,
          submittedByInitial: person?.initial || '',
          submittedByName: person?.name || '',
        };
      }

      const payload = {
        ...personFields,
        submittedByRole: recordForm.category.toLowerCase(),
        role: recordForm.category,
        submittedAt,
        submittedDateLocal: recordForm.date,
        method: recordForm.method,
        schoolYear,
        enteredByEmail: auth.currentUser?.email || '',
        enteredByName: ownName,
        enteredAt: new Date().toISOString(),
      };

      // A record entered or corrected by hand has been vouched for by the person doing it, so it is
      // verified on the spot rather than left pending -- provided they actually hold Verify rights,
      // which the module tracks separately from Edit.
      if (canVerifyCategory(recordForm.category)) {
        payload.verified = true;
        payload.verifiedByEmail = auth.currentUser?.email || '';
        payload.verifiedByName = ownName;
        payload.verifiedAt = new Date().toISOString();
      }

      if (editingRecordId) {
        await setDoc(doc(db, 'schools', activeSchoolId, 'attendance_record', editingRecordId), payload, { merge: true });
      } else {
        if (payload.verified === undefined) payload.verified = false;
        payload.submitIp = 'N/A';
        payload.qrId = null;
        await setDoc(doc(collection(db, 'schools', activeSchoolId, 'attendance_record')), { ...payload, ...ownerStamp() });
      }
      setShowRecordModal(false);
    } catch (e) {
      console.error('Error saving attendance record', e);
      alert(t('attendance.recordSaveFailed', { message: e.message }));
    }
    setSavingRecord(false);
  };

  const handleDeleteRecord = async (record) => {
    if (!window.confirm(t('attendance.deleteRecordConfirm', { name: record.submittedByName || t('attendance.thisPerson') }))) return;
    try {
      await deleteDoc(doc(db, 'schools', activeSchoolId, 'attendance_record', record.id));
    } catch (e) {
      console.error('Error deleting attendance record', e);
      alert(t('attendance.deleteRecordFailed', { message: e.message }));
    }
  };

  // --- QR display tab ---
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [qrGeneratedAt, setQrGeneratedAt] = useState(null);
  const [countdownMs, setCountdownMs] = useState(QR_VALIDITY_MS);
  const [qrRecords, setQrRecords] = useState([]);
  const [loadingQrRecords, setLoadingQrRecords] = useState(false);

  // History of every QR code generated on the selected day (defaults to today, same date picker
  // used by the Teacher/Staff/Student tabs).
  useEffect(() => {
    if (!activeSchoolId || !selectedDate || activeTab !== 'qr') return;
    setLoadingQrRecords(true);
    const q = query(collection(db, 'schools', activeSchoolId, 'qr_record'), where('generatedDateLocal', '==', selectedDate));
    const unsub = onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach(d => list.push({ id: d.id, ...d.data() }));
      list.sort((a, b) => (b.generatedAt || '').localeCompare(a.generatedAt || ''));
      setQrRecords(list);
      setLoadingQrRecords(false);
    }, (e) => { console.error('Error loading QR history', e); setLoadingQrRecords(false); });
    return () => unsub();
  }, [activeSchoolId, selectedDate, activeTab]);

  useEffect(() => {
    if (!activeSchoolId) return;
    const fetchYears = async () => {
      try {
        const snap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'academic_years')));
        const list = [];
        snap.forEach(d => list.push({ uid: d.id, ...d.data() }));
        list.sort((a, b) => (a.yearId || '').localeCompare(b.yearId || ''));
        setAcademicYears(list);
        if (list.length > 0) {
          const currentYear = list.find(y => y.cur_year) || list[list.length - 1];
          setSchoolYear(currentYear.yearId);
        }
      } catch (e) {
        console.error('Error loading academic years', e);
      }
    };
    fetchYears();
  }, [activeSchoolId]);

  // --- My own attendance record history ---
  useEffect(() => {
    if (!activeSchoolId || !ownEmail) { setMyRecords([]); return; }
    setLoadingMyRecords(true);
    const q = query(collection(db, 'schools', activeSchoolId, 'attendance_record'), where('submittedByEmail', '==', ownEmail));
    const unsub = onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach(d => list.push({ id: d.id, ...d.data() }));
      list.sort((a, b) => (b.submittedAt || '').localeCompare(a.submittedAt || ''));
      setMyRecords(list);
      setLoadingMyRecords(false);
    }, (e) => { console.error('Error loading my attendance records', e); setLoadingMyRecords(false); });
    return () => unsub();
  }, [activeSchoolId, ownEmail]);

  const myRecordsForYear = useMemo(
    () => myRecords.filter(r => !schoolYear || r.schoolYear === schoolYear),
    [myRecords, schoolYear]
  );

  // --- Submit an attendance record (Manual button, or a successful QR scan) ---
  const submitAttendance = async (method, qrId = null) => {
    // Two marks a day, told apart by the clock. A second scan in the same half is a mistake, and
    // saying which one is already there is what makes it answerable.
    const now0 = new Date();
    const half = halfOf(now0.toISOString(), halfCutoff);
    const mine = myRecords.filter(r => r.submittedDateLocal === localDateString(now0));
    const clash = blockingRecord(mine, half);
    if (clash) {
      alert(t(half === DAY_HALF.IN ? 'attendance.signedInAt' : 'attendance.signedOutAt', {
        time: formatTime(clash.submittedAt),
      }));
      return;
    }
    setSubmitting(true);
    try {
      let submitIp = 'Unknown';
      try {
        const res = await fetch('https://api.ipify.org?format=json');
        const data = await res.json();
        submitIp = data.ip || 'Unknown';
      } catch (e) {
        // Best-effort only -- no IP lookup service reachable, keep 'Unknown'.
      }

      const now = new Date();
      const payload = {
        // Stored, and read back by every screen that shows the day -- never re-derived from the
        // timestamp, or moving the cut-off would rewrite history.
        session: half,
        submittedAt: now.toISOString(),
        submittedDateLocal: localDateString(now),
        submittedByEmail: auth.currentUser?.email || '',
        submittedByInitial: ownInitial,
        submittedBySid: ownSid,
        submittedByName: ownName,
        submittedByRole: ownRole || 'staff',
        role: normalizeRoleBucket(ownRole),
        submitIp,
        method,
        qrId,
        verified: false,
        schoolYear,
      };
      // One document per person per half per day. A second write lands on the same id, and the
      // rules refuse it for anyone who may not update -- the duplicate becomes impossible rather
      // than merely guarded against.
      const id = attendanceDocId(payload);
      const ref = id
        ? doc(db, 'schools', activeSchoolId, 'attendance_record', id)
        : doc(collection(db, 'schools', activeSchoolId, 'attendance_record'));
      await setDoc(ref, payload);
      alert(t('attendance.submitted'));
    } catch (e) {
      console.error('Error submitting attendance', e);
      alert(t('attendance.submitFailed', { message: e.message }));
    }
    setSubmitting(false);
  };

  const handleManualCheckIn = () => {
    if (!window.confirm(t('attendance.manualConfirm'))) return;
    submitAttendance('Manual');
  };

  // --- QR scanning (My Attendance tab) ---
  useEffect(() => {
    if (!showScanModal) return;
    scanHandledRef.current = false;
    setScanError('');
    const scanner = new Html5Qrcode(qrRegionId);
    html5QrRef.current = scanner;

    scanner.start(
      { facingMode: 'environment' },
      { fps: 10, qrbox: 250 },
      (decodedText) => handleScanSuccess(decodedText),
      () => { /* per-frame decode miss, ignore */ }
    ).catch(err => setScanError('Could not access camera: ' + err.message));

    // html5-qrcode's stop() throws SYNCHRONOUSLY (not a rejected promise) if the scanner isn't
    // currently running -- e.g. it never started (camera denied/unavailable) or a successful scan
    // already stopped it in handleScanSuccess. Chaining .catch() after .stop() doesn't help since
    // the throw happens before .then()/.catch() are ever reached, so this must be a plain try/catch
    // guarded by isScanning, or it surfaces as an uncaught error that takes down the whole page.
    return () => {
      const s = html5QrRef.current;
      if (!s) return;
      try {
        if (s.isScanning) {
          s.stop().then(() => { try { s.clear(); } catch (e) { /* no-op */ } }).catch(() => { /* no-op */ });
        } else {
          s.clear();
        }
      } catch (e) { /* scanner already stopped/never started */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showScanModal]);

  const handleScanSuccess = async (decodedText) => {
    if (scanHandledRef.current) return;
    scanHandledRef.current = true;
    const scanner = html5QrRef.current;
    try { if (scanner?.isScanning) await scanner.stop(); } catch (e) { /* already stopped */ }
    setShowScanModal(false);

    let payload;
    try {
      payload = JSON.parse(decodedText);
    } catch (e) {
      alert(t('attendance.qrNotValid'));
      return;
    }
    if (payload.s !== activeSchoolId) {
      alert(t('attendance.qrOtherSchool'));
      return;
    }
    if (!payload.id) {
      alert(t('attendance.qrNotValid'));
      return;
    }

    // The scanned payload's own claims aren't trusted -- freshness is checked against the
    // server-side qr_record doc written when this code was actually generated. A fabricated id
    // with no matching doc is rejected outright, which is the real "is this QR fake" check.
    let qrRecordData;
    try {
      const qrSnap = await getDoc(doc(db, 'schools', activeSchoolId, 'qr_record', payload.id));
      if (!qrSnap.exists()) {
        alert(t('attendance.qrUnverified'));
        return;
      }
      qrRecordData = qrSnap.data();
    } catch (e) {
      console.error('Error verifying QR code', e);
      alert(t('attendance.qrCheckFailed'));
      return;
    }
    const generatedAt = new Date(qrRecordData.generatedAt).getTime();
    if (Number.isNaN(generatedAt) || Date.now() - generatedAt > QR_VALIDITY_MS) {
      alert(t('attendance.qrExpired'));
      return;
    }
    await submitAttendance('QR', payload.id);
    setActiveTab('my_attendance');
  };

  // --- QR display tab: regenerate every QR_VALIDITY_MS, countdown every second. Each generation
  // is recorded in qr_record so a scan can be traced back to a genuine, server-known code. ---
  const regenerateQr = async () => {
    if (!activeSchoolId) return;
    const now = new Date();
    try {
      const qrDocRef = doc(collection(db, 'schools', activeSchoolId, 'qr_record'));
      await setDoc(qrDocRef, {
        generatedAt: now.toISOString(),
        generatedDateLocal: localDateString(now),
        generatedByEmail: auth.currentUser?.email || '',
        generatedByName: ownName,
        schoolId: activeSchoolId,
      });
      const payload = JSON.stringify({ s: activeSchoolId, id: qrDocRef.id });
      const dataUrl = await QRCode.toDataURL(payload, { width: 320, margin: 2 });
      setQrDataUrl(dataUrl);
      setQrGeneratedAt(now);
    } catch (e) {
      console.error('Error generating QR code', e);
    }
  };

  useEffect(() => {
    if (activeTab !== 'qr' || !activeSchoolId) return;
    regenerateQr();
    const interval = setInterval(regenerateQr, QR_VALIDITY_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, activeSchoolId]);

  useEffect(() => {
    if (!qrGeneratedAt || activeTab !== 'qr') return;
    const tick = setInterval(() => {
      setCountdownMs(Math.max(0, QR_VALIDITY_MS - (Date.now() - qrGeneratedAt.getTime())));
    }, 1000);
    return () => clearInterval(tick);
  }, [qrGeneratedAt, activeTab]);

  // --- QR tab: history of codes generated on the selected day (shares the same date picker used
  // by Teacher/Staff/Student), so an admin can see exactly what was generated when and by whom. ---
  const [qrHistory, setQrHistory] = useState([]);
  const [loadingQrHistory, setLoadingQrHistory] = useState(false);

  useEffect(() => {
    if (activeTab !== 'qr' || !activeSchoolId || !selectedDate) return;
    setLoadingQrHistory(true);
    const q = query(collection(db, 'schools', activeSchoolId, 'qr_record'), where('generatedDateLocal', '==', selectedDate));
    const unsub = onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach(d => list.push({ id: d.id, ...d.data() }));
      list.sort((a, b) => (b.generatedAt || '').localeCompare(a.generatedAt || ''));
      setQrHistory(list);
      setLoadingQrHistory(false);
    }, (e) => { console.error('Error loading QR history', e); setLoadingQrHistory(false); });
    return () => unsub();
  }, [activeTab, activeSchoolId, selectedDate]);

  const tabs = [
    { id: 'my_attendance', label: t('attendance.tabs.myAttendance'), icon: UserCheck },
    // Everything past My Attendance reads the school registers, which are staff-only at the data
    // layer -- a student opening them would get an empty table and a permission error. The QR
    // scanner they do need lives inside My Attendance, not in the QR tab (which issues codes).
    ...(isStudent ? [] : [
      { id: 'teacher', label: t('attendance.tabs.teacher'), icon: BookOpen },
      { id: 'staff', label: t('attendance.tabs.staff'), icon: Briefcase },
      { id: 'student', label: t('attendance.tabs.student'), icon: GraduationCap },
    ]),
    // Leave writes to leave_record, which Firestore rules restrict to staff -- a student opening
    // this tab would only get a permission error, so it isn't offered to them.
    ...(isStudent ? [] : [{ id: 'leave', label: t('attendance.tabs.leave'), icon: CalendarX }]),
    ...(isStudent ? [] : [
      { id: 'settings', label: t('attendance.tabs.settings'), icon: SettingsIcon },
      // Two modes, and they are opposites. QR puts a five-minute code on a screen and each person
      // records their own arrival with their own phone -- the sports-day shape. Scan is the reverse:
      // staff hold the device and scan the card each person presents, which is the ordinary school
      // day, when students are not meant to have phones out at all. A school uses both, in the same
      // week, for different occasions.
      { id: 'qr', label: t('attendance.tabs.qr'), icon: QrCode },
      { id: 'scan', label: t('attendance.tabs.scan'), icon: ScanLine },
      { id: 'cards', label: t('attendance.tabs.cards'), icon: IdCard },
    ]),
  ];

  return (
    <div className="animate-fade-in new-theme" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Tabs Bar */}
      <div className="theme-tabs-bar" style={{ minHeight: '66px' }}>
        <div className="theme-tabs-group">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`theme-tab ${isActive ? 'active' : ''}`}
                style={{ whiteSpace: 'nowrap' }}
              >
                <div className="theme-tab-icon"><Icon size={18} /></div>
                {tab.label}
              </button>
            );
          })}
        </div>

        <HeaderActionsPortal>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {['teacher', 'staff', 'student', 'qr', 'leave'].includes(activeTab) && (
            <input type="date" className="input-field" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} style={{ width: '160px' }} />
          )}
          {['teacher', 'staff', 'student'].includes(activeTab) && canEditCategory(activeTab.charAt(0).toUpperCase() + activeTab.slice(1)) && (
            <button className="btn-primary" onClick={() => openAddRecordModal(activeTab.charAt(0).toUpperCase() + activeTab.slice(1))} style={{ width: 'auto', display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 1rem', whiteSpace: 'nowrap' }}>
              <Plus size={16} /> {t('attendance.addRecord')}
            </button>
          )}
          {activeTab === 'leave' && (
            <>
              <button className="btn-secondary" onClick={exportLeaveCsv} title={t('attendance.exportCsv')} style={{ padding: '0.5rem 0.7rem', width: 'auto' }}>
                <Download size={16} />
              </button>
              <button className="btn-primary" onClick={() => handleSaveLeave(true)} disabled={savingLeave || leaveRows.length === 0} style={{ padding: '0.5rem 1rem', width: 'auto', whiteSpace: 'nowrap' }}>
                <Save size={15} /> {t('attendance.confirmAll')}
              </button>
            </>
          )}
          <select className="input-field" value={schoolYear} onChange={e => setSchoolYear(e.target.value)} style={{ width: '140px' }}>
            {academicYears.length === 0 ? (
              <option value="">{t('attendance.noYears')}</option>
            ) : academicYears.map(y => <option key={y.uid} value={y.yearId}>{y.yearId}</option>)}
          </select>
          {activeTab === 'settings' && (
            <button className="btn-primary" onClick={handleSaveAttendanceSettings} disabled={savingSettings || !settingsLoaded} style={{ padding: '0.5rem 1.25rem' }}>
              {savingSettings ? t('common.saving') : t('attendance.saveSettings')}
            </button>
          )}
        </div>
        </HeaderActionsPortal>
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 1.5rem 1.5rem' }}>

        {activeTab === 'my_attendance' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '700', margin: 0 }}>{schoolYear ? t('attendance.myTitleYear', { year: schoolYear }) : t('attendance.myTitle')}</h2>

            <div style={{ backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '0.75rem' : '1.5rem', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <button
                className="btn-primary"
                onClick={() => setShowScanModal(true)}
                disabled={submitting || !hasCamera}
                title={!hasCamera ? t('attendance.noCameraTitle') : undefined}
                style={{ width: 'auto', display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.5rem' }}
              >
                <Camera size={18} /> {t('attendance.scanQr')}
              </button>
              {!isStudent && (
                <button
                  className="btn-outline"
                  onClick={handleManualCheckIn}
                  disabled={submitting}
                  style={{ width: 'auto', display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.5rem' }}
                >
                  <Edit3 size={18} /> {t('attendance.manualCheckIn')}
                </button>
              )}
              </div>
              {!hasCamera && (
                <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                  {isStudent ? t('attendance.noCameraStudent') : t('attendance.noCameraStaff')}
                </p>
              )}
            </div>

            <div className="table-container" style={{ backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                <thead>
                  <tr style={{ backgroundColor: 'var(--color-bg-app)', borderBottom: '1px solid var(--color-border)', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('attendance.dateTime')}</th>
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('attendance.method')}</th>
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('attendance.qrId')}</th>
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('attendance.ipAddress')}</th>
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('attendance.verifiedCol')}</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingMyRecords ? (
                    <tr><td colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>{t('common.loading')}</td></tr>
                  ) : myRecordsForYear.length === 0 ? (
                    <tr><td colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>{t('attendance.noRecordsThisYear')}</td></tr>
                  ) : myRecordsForYear.map((r, i) => (
                    <tr key={r.id} style={{ borderBottom: i === myRecordsForYear.length - 1 ? 'none' : '1px solid var(--color-border)', fontSize: '0.875rem' }}>
                      <td style={{ padding: '0.75rem 1rem' }}>{formatDateTime(r.submittedAt)}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>{r.method ? methodWord(r.method) : '-'}</td>
                      <td style={{ padding: '0.75rem 1rem', color: 'var(--color-text-muted)', fontSize: '0.8rem', fontFamily: 'monospace' }}>{r.method === 'QR' ? (r.qrId || '-') : '-'}</td>
                      <td style={{ padding: '0.75rem 1rem', color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', gap: '0.3rem' }}><MapPin size={13} />{r.submitIp || t('attendance.unknownIp')}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>
                        {r.verified ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', color: 'var(--color-success)', fontSize: '0.8rem', fontWeight: '600' }}><CheckCircle2 size={14} /> {t('attendance.verified')}</span>
                        ) : (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', color: 'var(--color-text-muted)', fontSize: '0.8rem', fontWeight: '600' }}><XCircle size={14} /> {t('attendance.pending')}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'scan' && (
          <AttendanceScan
            activeSchoolId={activeSchoolId}
            isMobile={isMobile}
            schoolYear={schoolYear}
            ownEmail={ownEmail}
            ownName={ownName}
            canVerify={canVerifyCategory('Student')}
            todayIso={localDateString()}
          />
        )}

        {activeTab === 'cards' && (
          <AttendanceCards
            activeSchoolId={activeSchoolId}
            isMobile={isMobile}
            students={studentList}
            staffList={staffList}
            canManage={isAdmin || isModuleAdmin}
          />
        )}

        {activeTab === 'qr' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem', textAlign: 'center' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '700', margin: 0 }}>{t('attendance.qrTitle')}</h2>
              <div style={{ backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '1rem' : '2rem', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
                {qrDataUrl ? (
                  <img src={qrDataUrl} alt={t('attendance.qrAlt')} style={{ width: 320, height: 320 }} />
                ) : (
                  <div style={{ width: 320, height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-muted)' }}>{t('attendance.generating')}</div>
                )}
                <p style={{ margin: 0, color: 'var(--color-text-muted)' }}>{t('attendance.refreshesIn')} <strong>{formatCountdown(countdownMs)}</strong></p>
                <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--color-text-muted)', maxWidth: '360px' }}>{t('attendance.qrHowTo')}</p>
              </div>
            </div>

            <div>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: '0 0 1rem' }}>{t('attendance.qrGenerated', { date: selectedDate })}</h3>
              <div className="table-container" style={{ backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--color-bg-app)', borderBottom: '1px solid var(--color-border)', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                      <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('attendance.qrId')}</th>
                      <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('attendance.generatedAt')}</th>
                      <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('attendance.generatedBy')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingQrHistory ? (
                      <tr><td colSpan={3} style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>{t('common.loading')}</td></tr>
                    ) : qrHistory.length === 0 ? (
                      <tr><td colSpan={3} style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>{t('attendance.noQrCodes')}</td></tr>
                    ) : qrHistory.map((q, i) => (
                      <tr key={q.id} style={{ borderBottom: i === qrHistory.length - 1 ? 'none' : '1px solid var(--color-border)', fontSize: '0.875rem' }}>
                        <td style={{ padding: '0.75rem 1rem', fontFamily: 'monospace', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>{q.id}</td>
                        <td style={{ padding: '0.75rem 1rem' }}>{formatDateTime(q.generatedAt)}</td>
                        <td style={{ padding: '0.75rem 1rem' }}>{q.generatedByName || q.generatedByEmail || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'teacher' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '700', margin: 0 }}>{t('attendance.teacherTitle', { date: selectedDate })}</h2>
            <AttendanceDayTable
              records={teacherDayRecords}
              loading={loadingDayRecords}
              canVerify={canVerifyCategory('Teacher')}
              onVerify={handleVerify}
              canEdit={canEditCategory('Teacher')}
              onEdit={(r) => openEditRecordModal('Teacher', r)}
              onDelete={handleDeleteRecord}
              emptyText={t('attendance.noTeacherRecords')}
              isMobile={isMobile}
            />
          </div>
        )}

        {activeTab === 'staff' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '700', margin: 0 }}>{t('attendance.staffTitle', { date: selectedDate })}</h2>
            <AttendanceDayTable
              records={staffDayRecords}
              loading={loadingDayRecords}
              canVerify={canVerifyCategory('Staff')}
              onVerify={handleVerify}
              canEdit={canEditCategory('Staff')}
              onEdit={(r) => openEditRecordModal('Staff', r)}
              onDelete={handleDeleteRecord}
              emptyText={t('attendance.noStaffRecords')}
              isMobile={isMobile}
            />
          </div>
        )}

        {activeTab === 'student' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '700', margin: 0 }}>{t('attendance.studentTitle', { date: selectedDate })}</h2>
            {loadingDayRecords ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>{t('common.loading')}</div>
            ) : studentGroupsByClass.length === 0 ? (
              <div style={{ textAlign: 'center', padding: isMobile ? '1.5rem' : '3rem', color: 'var(--color-text-muted)', backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)' }}>{t('attendance.noStudentRecords')}</div>
            ) : studentGroupsByClass.map(group => {
              const isExpanded = expandedClasses.has(group.className);
              return (
                <div key={group.className} style={{ backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', overflow: 'hidden' }}>
                  <button
                    type="button"
                    onClick={() => toggleClassExpanded(group.className)}
                    style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.25rem', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                  >
                    <span style={{ fontWeight: '700', fontSize: '1rem' }}>{group.className} <span style={{ color: 'var(--color-text-muted)', fontWeight: '500', fontSize: '0.85rem' }}>{t('attendance.recordCount', { count: group.records.length })}</span></span>
                    {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </button>
                  {isExpanded && (
                    <div style={{ padding: '0 1.25rem 1.25rem' }}>
                      <AttendanceDayTable
                        records={group.records}
                        loading={false}
                        canVerify={canVerifyCategory('Student')}
                        onVerify={handleVerify}
                        canEdit={canEditCategory('Student')}
                        onEdit={(r) => openEditRecordModal('Student', r)}
                        onDelete={handleDeleteRecord}
                        studentMap={studentMap}
                        showStudentName
                        emptyText={t('attendance.noRecords')}
                        isMobile={isMobile}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {activeTab === 'leave' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '700', margin: 0 }}>{t('attendance.leaveTitle', { date: selectedDate })}</h2>
              <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.85rem', color: 'var(--color-text-muted)', flexWrap: 'wrap' }}>
                <span>{t('attendance.peopleCount', { count: leaveSummary.total })}</span>
                <span style={{ color: leaveSummary.missing ? 'var(--color-danger)' : 'inherit' }}>{t('attendance.withoutAttendance', { count: leaveSummary.missing })}</span>
                <span>{t('attendance.confirmedCount', { count: leaveSummary.confirmed })}</span>
              </div>
            </div>

            {!isSchoolDay && calendarEvents.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1rem', backgroundColor: 'var(--color-danger-bg)', color: 'var(--color-warning-text)', borderRadius: isMobile ? 0 : 'var(--radius-md)', fontSize: '0.875rem' }}>
                <AlertTriangle size={16} />
                {t('attendance.notSchoolDay', { date: selectedDate, year: schoolYear })}
              </div>
            )}

            <div style={{ backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', overflowX: 'auto' }}>
              {loadingLeave ? (
                <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>{t('common.loading')}</div>
              ) : leaveRows.length === 0 ? (
                <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>{t('attendance.noStaffProfiles')}</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--color-bg-app)', borderBottom: '1px solid var(--color-border)', fontSize: '0.8rem', color: 'var(--color-text-muted)', textAlign: 'left' }}>
                      <th style={{ padding: '0.65rem 0.75rem', whiteSpace: 'nowrap' }}>{t('attendance.initial')}</th>
                      <th style={{ padding: '0.65rem 0.75rem', whiteSpace: 'nowrap' }}>{t('attendance.name')}</th>
                      <th style={{ padding: '0.65rem 0.75rem', whiteSpace: 'nowrap' }}>{t('attendance.type')}</th>
                      <th style={{ padding: '0.65rem 0.75rem', whiteSpace: 'nowrap' }}>{t('attendance.checkIn')}</th>
                      <th style={{ padding: '0.65rem 0.75rem', whiteSpace: 'nowrap' }}>{t('attendance.status')}</th>
                      <th style={{ padding: '0.65rem 0.75rem', whiteSpace: 'nowrap' }}>{t('attendance.leaveType')}</th>
                      <th style={{ padding: '0.65rem 0.75rem', whiteSpace: 'nowrap' }}>{t('attendance.remark')}</th>
                      <th style={{ padding: '0.65rem 0.75rem', whiteSpace: 'nowrap' }}>{t('attendance.confirmedCol')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaveRows.map(r => {
                      const td = { padding: '0.5rem 0.75rem', borderTop: '1px solid var(--color-border)', whiteSpace: 'nowrap' };
                      const cellInput = { margin: 0, padding: '0.35rem 0.5rem', height: 'auto', fontSize: '0.85rem' };
                      return (
                        <tr key={r.initial} style={{ backgroundColor: !r.hasAttendance && !r.confirmed ? 'var(--color-danger-bg)' : 'transparent' }}>
                          <td style={{ ...td, fontWeight: '600' }}>{r.initial}</td>
                          <td style={td}>{r.name}</td>
                          <td style={td}>{categoryWord(r.category)}</td>
                          <td style={td}>
                            {r.hasAttendance
                              ? (
                                <span>
                                  {r.checkIn} <span style={{ color: 'var(--color-text-muted)', fontSize: '0.75rem' }}>({methodWord(r.method)})</span>
                                  {/* Saving this audit does not verify it, so say so here -- otherwise
                                      a pending check-in is invisible and stays pending forever. */}
                                  {!r.attendanceVerified && (
                                    <span style={{ color: 'var(--color-warning-text)', fontSize: '0.75rem' }}> · {t('attendance.pendingVerification')}</span>
                                  )}
                                  {/* The second mark of the day. Absent is worth saying out loud --
                                      it is the whole reason for having a sign-out. */}
                                  <span style={{ display: 'block', fontSize: '0.75rem', color: r.signOut ? 'var(--color-text-muted)' : 'var(--color-warning-text)' }}>
                                    {t('attendance.signOutCol')}: {r.signOut || t('attendance.noSignOut')}
                                  </span>
                                </span>
                              )
                              : <span style={{ color: 'var(--color-danger)' }}>{t('attendance.noRecord')}</span>}
                          </td>
                          <td style={td}>
                            <select className="input-field" style={{ ...cellInput, width: '130px' }} value={r.status} onChange={e => updateLeaveRow(r.initial, { status: e.target.value })}>
                              {LEAVE_STATUSES.map(v => <option key={v} value={v}>{statusWord(v)}</option>)}
                            </select>
                          </td>
                          <td style={td}>
                            <select className="input-field" style={{ ...cellInput, width: '170px' }} value={r.leaveType} onChange={e => updateLeaveRow(r.initial, { leaveType: e.target.value })}>
                              <option value="">—</option>
                              {LEAVE_TYPES.map(v => <option key={v} value={v}>{leaveTypeWord(v)}</option>)}
                            </select>
                          </td>
                          <td style={td}>
                            <input className="input-field" style={{ ...cellInput, width: '180px' }} value={r.remark} onChange={e => updateLeaveRow(r.initial, { remark: e.target.value })} placeholder="—" />
                          </td>
                          <td style={td}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', userSelect: 'none' }}>
                              <input
                                type="checkbox"
                                checked={r.confirmed}
                                onChange={e => setLeaveConfirmed(r.initial, e.target.checked)}
                                style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: 'var(--color-primary)' }}
                              />
                              <span style={{ color: r.confirmed ? 'var(--color-success-text)' : 'var(--color-text-muted)', fontWeight: r.confirmed ? '600' : '400' }}>
                                {r.confirmed ? t('attendance.confirmed') : t('attendance.pending')}
                              </span>
                            </label>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {leaveRows.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                <button className="btn-secondary" onClick={() => loadLeave()} disabled={loadingLeave} style={{ width: 'auto', padding: '0.55rem 1rem' }}>
                  <RefreshCw size={15} /> Reload
                </button>
                <button className="btn-secondary" onClick={() => handleSaveLeave(false)} disabled={savingLeave} style={{ width: 'auto', padding: '0.55rem 1rem' }}>
                  <Save size={15} /> Save
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'settings' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', width: '100%' }}>
            {/* Where the morning ends. It decides which of the day's two marks a scan becomes,
                so it belongs beside the permissions rather than buried anywhere else. */}
            <div style={{ backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '0.75rem' : '1.5rem', borderRadius: isMobile ? 0 : 'var(--radius-lg)' }}>
              <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: '600', marginBottom: '0.35rem' }}>
                {t('attendance.halfCutoff')}
              </label>
              <input
                type="time"
                className="input-field"
                value={halfCutoff}
                onChange={e => setHalfCutoff(e.target.value)}
                style={{ width: '140px', margin: 0 }}
              />
              <p style={{ margin: '0.5rem 0 0', fontSize: '0.8rem', color: 'var(--color-text-muted)', lineHeight: 1.6, maxWidth: '60ch' }}>
                {t('attendance.halfCutoffHint')}
              </p>
              {minutesOfDay(halfCutoff) === null && (
                <p style={{ margin: '0.35rem 0 0', fontSize: '0.8rem', color: 'var(--color-danger)' }}>
                  {t('attendance.badCutoff')}
                </p>
              )}
            </div>

            <h2 style={{ fontSize: '1.25rem', fontWeight: '700', margin: 0 }}>{t('attendance.permissions')}</h2>
            <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>
              {t('attendance.permissionsIntro')}
            </p>
            {['Student', 'Teacher', 'Staff'].map(category => (
              <div key={category} style={{ backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '0.75rem' : '1.5rem', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: '0 0 1rem' }}>{t('attendance.categoryRecords', { category: categoryWord(category) })}</h3>
                <table style={{ borderCollapse: 'collapse', width: '100%', maxWidth: '420px', maxHeight: isMobile ? '92vh' : 'none', overflowY: 'auto' }}>
                  <thead>
                    <tr style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', textAlign: 'left' }}>
                      <th style={{ padding: '0.5rem 0.75rem' }}>{t('attendance.role')}</th>
                      <th style={{ padding: '0.5rem 0.75rem', textAlign: 'center' }}>{t('attendance.edit')}</th>
                      <th style={{ padding: '0.5rem 0.75rem', textAlign: 'center' }}>{t('attendance.verify')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ACTOR_ROLES.map(role => (
                      <tr key={role} style={{ borderTop: '1px solid var(--color-border)' }}>
                        <td style={{ padding: '0.6rem 0.75rem', fontWeight: '600' }}>{role}</td>
                        <td style={{ padding: '0.6rem 0.75rem', textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={role === 'Admin' || (permissions[category]?.edit || []).includes(role)}
                            disabled={role === 'Admin'}
                            onChange={() => togglePermission(category, 'edit', role)}
                            style={{ width: '16px', height: '16px', cursor: role === 'Admin' ? 'default' : 'pointer' }}
                          />
                        </td>
                        <td style={{ padding: '0.6rem 0.75rem', textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={role === 'Admin' || (permissions[category]?.verify || []).includes(role)}
                            disabled={role === 'Admin'}
                            onChange={() => togglePermission(category, 'verify', role)}
                            style={{ width: '16px', height: '16px', cursor: role === 'Admin' ? 'default' : 'pointer' }}
                          />
                        </td>
                      </tr>
                    ))}
                    <tr style={{ borderTop: '1px solid var(--color-border)' }}>
                      <td style={{ padding: '0.6rem 0.75rem', fontWeight: '600' }}>{t('attendance.others')}</td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'center' }}>
                        <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                        {t('attendance.namedInAccessControl')}
                      </span>
                      </td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'center' }}>
                        <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                        {t('attendance.namedInAccessControl')}
                      </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <p style={{ margin: '0.75rem 0 0', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                  {t('attendance.othersNote')}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Manage "Others" staff modal */}
      {showOthersModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'var(--color-overlay)', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: isMobile ? 'flex-end' : 'center', padding: isMobile ? 0 : '2rem' }}>
          <div style={{ backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '1rem' : '2rem', borderRadius: isMobile ? 'var(--radius-lg) var(--radius-lg) 0 0' : 'var(--radius-lg)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', width: '100%', maxWidth: '420px', display: 'flex', flexDirection: 'column', maxHeight: isMobile ? '92vh' : '80vh' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ fontSize: '1.15rem', fontWeight: '700', margin: 0 }}>
                {t('attendance.othersModalTitle', {
                  action: showOthersModal.action === 'edit' ? t('attendance.edit') : t('attendance.verify'),
                  category: categoryWord(showOthersModal.category),
                })}
              </h3>
              <button type="button" onClick={() => setShowOthersModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex' }}>
                <X size={20} />
              </button>
            </div>
            <input
              className="input-field"
              placeholder={t('attendance.searchStaff')}
              value={othersSearch}
              onChange={e => setOthersSearch(e.target.value)}
              style={{ marginBottom: '0.75rem' }}
            />
            <div style={{ overflowY: 'auto', flex: 1, border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
              {othersSearchResults.length === 0 ? (
                <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>{t('attendance.noStaffMatch')}</div>
              ) : othersSearchResults.map(s => {
                const key = `${showOthersModal.action}Staff`;
                const checked = (permissions[showOthersModal.category]?.[key] || []).includes(s.email);
                return (
                  <label key={s.email} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--color-border)', cursor: 'pointer', fontSize: '0.875rem' }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleOthersStaff(showOthersModal.category, showOthersModal.action, s.email)}
                      style={{ width: '15px', height: '15px', cursor: 'pointer' }}
                    />
                    <span>{s.name}{s.initial ? ` (${s.initial})` : ''}</span>
                  </label>
                );
              })}
            </div>
            <button type="button" className="btn-primary" style={{ marginTop: '1rem' }} onClick={() => setShowOthersModal(null)}>{t('attendance.done')}</button>
          </div>
        </div>
      )}

      {/* Add / Edit Attendance Record Modal */}
      {showRecordModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'var(--color-overlay)', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: isMobile ? 'flex-end' : 'center', padding: isMobile ? 0 : '2rem' }}>
          <div style={{ backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '1rem' : '2rem', borderRadius: isMobile ? 'var(--radius-lg) var(--radius-lg) 0 0' : 'var(--radius-lg)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', width: '100%', maxWidth: '480px', maxHeight: isMobile ? '92vh' : 'none', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '700', margin: 0 }}>{editingRecordId
                ? t('attendance.editRecordTitle', { category: categoryWord(recordForm.category) })
                : t('attendance.addRecordTitle', { category: categoryWord(recordForm.category) })}</h3>
              <button type="button" onClick={() => setShowRecordModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex' }}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSaveRecordModal} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {recordForm.category === 'Student' ? (
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('attendance.classLabel')}</label>
                    <select className="input-field" value={recordForm.classFilter} onChange={e => setRecordForm(prev => ({ ...prev, classFilter: e.target.value, personKey: '' }))}>
                      <option value="">{t('attendance.selectClass')}</option>
                      {classOptions.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div style={{ flex: 2 }}>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('attendance.studentLabel')}</label>
                    <select className="input-field" value={recordForm.personKey} onChange={e => setRecordForm(prev => ({ ...prev, personKey: e.target.value }))} disabled={!recordForm.classFilter}>
                      <option value="">{t('attendance.selectStudent')}</option>
                      {recordFormClassStudents.map(s => <option key={s.sid} value={s.sid}>{s.classNumber} - {s.name} {s.chineseName}</option>)}
                    </select>
                  </div>
                </div>
              ) : (
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{categoryWord(recordForm.category)}</label>
                  <select className="input-field" value={recordForm.personKey} onChange={e => setRecordForm(prev => ({ ...prev, personKey: e.target.value }))}>
                    <option value="">{t('attendance.selectPerson')}</option>
                    {(recordForm.category === 'Teacher' ? teacherStaffOptions : nonTeacherStaffOptions).map(s => (
                      <option key={s.email} value={s.email}>{s.name}{s.initial ? ` (${s.initial})` : ''}</option>
                    ))}
                  </select>
                </div>
              )}

              <div style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('attendance.dateLabel')}</label>
                  <input type="date" className="input-field" value={recordForm.date} onChange={e => setRecordForm(prev => ({ ...prev, date: e.target.value }))} required />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('attendance.timeLabel')}</label>
                  <input type="time" className="input-field" value={recordForm.time} onChange={e => setRecordForm(prev => ({ ...prev, time: e.target.value }))} required />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('attendance.methodLabel')}</label>
                <select className="input-field" value={recordForm.method} onChange={e => setRecordForm(prev => ({ ...prev, method: e.target.value }))}>
                  <option value="Admin">{t('attendance.methods.admin')}</option>
                  <option value="Manual">{t('attendance.methods.manual')}</option>
                  <option value="QR">{t('attendance.methods.qr')}</option>
                </select>
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                <button type="button" className="btn-outline" style={{ flex: 1 }} onClick={() => setShowRecordModal(false)}>{t('common.cancel')}</button>
                <button type="submit" className="btn-primary" style={{ flex: 1 }} disabled={savingRecord}>{savingRecord ? t('common.saving') : t('common.save')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Scan QR Code Modal */}
      {showScanModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'var(--color-overlay)', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: isMobile ? 'flex-end' : 'center', padding: isMobile ? 0 : '2rem' }}>
          <div style={{ backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '1rem' : '2rem', borderRadius: isMobile ? 'var(--radius-lg) var(--radius-lg) 0 0' : 'var(--radius-lg)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', width: '100%', maxWidth: '420px', maxHeight: isMobile ? '92vh' : 'none', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '700', margin: 0 }}>{t('attendance.scanQr')}</h3>
              <button type="button" onClick={() => setShowScanModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex' }}>
                <X size={20} />
              </button>
            </div>
            <div id={qrRegionId} style={{ width: '100%', borderRadius: 'var(--radius-md)', overflow: 'hidden' }} />
            {scanError && <p style={{ color: 'var(--color-danger)', fontSize: '0.85rem', marginTop: '1rem' }}>{scanError}</p>}
            <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginTop: '1rem' }}>{t('attendance.pointCamera')}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Attendance;
